import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import { useProducers, fetchAgentContact, fetchFirstPolicyAgentContact } from '../ProducerSection';
import { lookupCarrierIdSync, resolveCarrierIdByName } from '../InsurerSearchDropdown';
import InsurerNaicField from '../InsurerNaicField';
import { useSelectedClient } from '../../../../hooks/useSelectedClient';
import { mapFields, getPolicyAddresses, pickPolicyAddress } from '../acordFieldEngine';
import type { AcordFieldItem } from '../acordFieldEngine';
import { ACORD28_MAP, ACORD28_POLICY_MAP } from './acord28FieldMap';
import type { Acord28FormData } from '../../utils/acord28Payload';
import { formatUsPhone } from 'utilities/formatPhone';
import { parseAcord28StoredCoverages } from '../../utils/parseAcord28StoredCoverages';
import type { StoredCoverage, StoredPolicy } from '../../utils/parseAcord28StoredCoverages';

function parseLocalDate(v: string): Date {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(v);
}

type YesNoNa = 'yes' | 'no' | 'na' | '';

interface Acord28FormProps {
  showAcord101?: boolean;
  selectedPolicies?: string[];
  /** "Additional Named Insured(s): ..." text derived from selected policies' customFields — auto-fills the ACORD 101 remarks textarea */
  acord101AdditionalNamedInsureds?: string;
  selectedProperties?: string[];
  insuredId?: string;
  fileParentId?: string;
  isV2016?: boolean;
  editCoverages?: StoredCoverage[];
  editPolicies?: StoredPolicy[];
  onLoadingChange?: (loading: boolean) => void;
}

export interface Acord28FormRef {
  getFormData(): Acord28FormData;
  applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>): void;
}

const Acord28Form = React.forwardRef<Acord28FormRef, Acord28FormProps>(
function Acord28Form({ showAcord101 = false, selectedPolicies = [], acord101AdditionalNamedInsureds = '', selectedProperties = [], insuredId = '', fileParentId = 'B33EB550-0716-44F5-B395-0632203AE275', isV2016 = false, editCoverages, editPolicies, onLoadingChange }: Acord28FormProps, ref) {
  // Tracker #241: explain the blank cell while no policy is selected. Display only; never saved.
  const noPolicyPlaceholder = selectedPolicies.length === 0 ? 'Select a policy above or type a number' : undefined;
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [producerName, setProducerName] = React.useState('');
  const [filteredProducerOptions, setFilteredProducerOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [producerAddr1, setProducerAddr1] = React.useState('');
  const [producerAddr2, setProducerAddr2] = React.useState('');
  const [producerAddr3, setProducerAddr3] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const { client } = useSelectedClient();
  
  const [producerPhone, setProducerPhone] = React.useState('');
  const [producerFax, setProducerFax] = React.useState('');
  const [producerEmail, setProducerEmail] = React.useState('');
  const [producerCode, setProducerCode] = React.useState('');
  const [producerSubCode, setProducerSubCode] = React.useState('');
  const [agencyCustomerId, setAgencyCustomerId] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [companyNaicId, setCompanyNaicId] = React.useState<string | null>(null);
  const [naicNo, setNaicNo] = React.useState('');
  const [policyType, setPolicyType] = React.useState('');
  const [loanNumber, setLoanNumber] = React.useState('');
  const [policyNumber, setPolicyNumber] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState<Date | null>(null);
  const [expirationDate, setExpirationDate] = React.useState<Date | null>(null);
  const [continuedUntilTerminated, setContinuedUntilTerminated] = React.useState(true);
  const [replacesPriorDate, setReplacesPriorDate] = React.useState<Date | null>(null);

  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddr1, setInsuredAddr1] = React.useState('');
  const [insuredAddr2, setInsuredAddr2] = React.useState('');
  const [insuredAddr3, setInsuredAddr3] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');
  const [additionalNamedInsureds, setAdditionalNamedInsureds] = React.useState('');
  const [propBuilding, setPropBuilding] = React.useState(false);
  const [propBusinessPersonalProp, setPropBusinessPersonalProp] = React.useState(false);
  const [locationDescription, setLocationDescription] = React.useState('');
  const [perilsInsured, setPerilsInsured] = React.useState(false);
  const [perilsBasic, setPerilsBasic] = React.useState(false);
  const [perilsBroad, setPerilsBroad] = React.useState(false);
  const [perilsSpecial, setPerilsSpecial] = React.useState(true);
  const [commPropCovCheck, setCommPropCovCheck] = React.useState(false);
  const [commPropCovText, setCommPropCovText] = React.useState('');
  const [commPropAmount, setCommPropAmount] = React.useState('');
  const [commPropDed, setCommPropDed] = React.useState('');
  const [businessIncome, setBusinessIncome] = React.useState(false);
  const [rentalValue, setRentalValue] = React.useState(false);
  const [birvYesNoNa, setBirvYesNoNa] = React.useState<YesNoNa>('');
  const [birvLimit, setBirvLimit] = React.useState('');
  const [birvLimit2, setBirvLimit2] = React.useState('');
  const [birvActualLoss, setBirvActualLoss] = React.useState(false);
  const [birvMonths, setBirvMonths] = React.useState('');

  const [blanketCoverage, setBlanketCoverage] = React.useState<YesNoNa>('');
  const [blanketAmount, setBlanketAmount] = React.useState('');

  const [terrorismCoverage, setTerrorismCoverage] = React.useState<YesNoNa>('');
  const [terrorismSpecificExclusion, setTerrorismSpecificExclusion] = React.useState<YesNoNa>('');
  const [domesticTerrorismExcluded, setDomesticTerrorismExcluded] = React.useState<YesNoNa>('');

  const [limitedFungus, setLimitedFungus] = React.useState<YesNoNa>('');
  const [limitedFungusLimit, setLimitedFungusLimit] = React.useState('');
  const [limitedFungusDed, setLimitedFungusDed] = React.useState('');

  const [fungusExclusion, setFungusExclusion] = React.useState<YesNoNa>('');
  const [fungusExclusionForm, setFungusExclusionForm] = React.useState('');
  const [fungusExclusionDate, setFungusExclusionDate] = React.useState<Date | null>(null);
  const [fungusExclusionText, setFungusExclusionText] = React.useState('');

  const [replacementCost, setReplacementCost] = React.useState<YesNoNa>('');
  const [agreedValue, setAgreedValue] = React.useState<YesNoNa>('');

  const [coinsurance, setCoinsurance] = React.useState<YesNoNa>('');
  const [coinsurancePercent, setCoinsurancePercent] = React.useState('');

  const [equipmentBreakdown, setEquipmentBreakdown] = React.useState<YesNoNa>('');
  const [equipmentBreakdownLimit, setEquipmentBreakdownLimit] = React.useState('');
  const [equipmentBreakdownDed, setEquipmentBreakdownDed] = React.useState('');

  const [ordinanceOrLaw, setOrdinanceOrLaw] = React.useState<YesNoNa>('');
  const [ordinanceLimit, setOrdinanceLimit] = React.useState('');
  const [ordinanceDed, setOrdinanceDed] = React.useState('');
  const [demolitionCostsYN, setDemolitionCostsYN] = React.useState<YesNoNa>('');
  const [demolitionLimit, setDemolitionLimit] = React.useState('');
  const [demolitionDed, setDemolitionDed] = React.useState('');
  const [incrCostYN, setIncrCostYN] = React.useState<YesNoNa>('');
  const [incrCostLimit, setIncrCostLimit] = React.useState('');
  const [incrCostDed, setIncrCostDed] = React.useState('');
  const [earthMovement, setEarthMovement] = React.useState<YesNoNa>('');
  const [earthMovementLimit, setEarthMovementLimit] = React.useState('');
  const [earthMovementDed, setEarthMovementDed] = React.useState('');
  const [flood, setFlood] = React.useState<YesNoNa>('');
  const [floodLimit, setFloodLimit] = React.useState('');
  const [floodDed, setFloodDed] = React.useState('');
  const [windHailInclYes, setWindHailInclYes] = React.useState(false);
  const [windHailInclNo, setWindHailInclNo] = React.useState(false);
  const [windHail, setWindHail] = React.useState<YesNoNa>('');
  const [windHailLimit, setWindHailLimit] = React.useState('');
  const [windHailDed, setWindHailDed] = React.useState('');
  const [namedStormInclYes, setNamedStormInclYes] = React.useState(false);
  const [namedStormInclNo, setNamedStormInclNo] = React.useState(false);
  const [namedStorm, setNamedStorm] = React.useState<YesNoNa>('');
  const [namedStormLimit, setNamedStormLimit] = React.useState('');
  const [namedStormDed, setNamedStormDed] = React.useState('');
  const [waiverSubrogation, setWaiverSubrogation] = React.useState<YesNoNa>('');
  const [aiEnabled, setAiEnabled] = React.useState(false);
  const [additionalInterestName, setAdditionalInterestName] = React.useState('');
  const [lenderServicingAgent, setLenderServicingAgent] = React.useState('');
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);
  const [remarks, setRemarks] = React.useState('');

  // ACORD 101
  const [acord101Agency, setAcord101Agency] = React.useState('');
  const [acord101NamedInsured, setAcord101NamedInsured] = React.useState('');
  const [acord101AdditionalRemarks, setAcord101AdditionalRemarks] = React.useState('');

  // Insured block: filled from the client only while a policy is selected (Farmers, 9/24) — a
  // certificate with no policy is created blank, not pre-filled. A selected policy's address
  // then overrides these through get-coverages; any policy counts, even one that fills no
  // coverage row. Edit mode: insured comes from stored XML, not the live client.
  React.useEffect(() => {
    if (!client) return;
    if (isEditMode.current) { setAcord101NamedInsured(client.name || ''); return; }
    if (!selectedPolicies || selectedPolicies.length === 0) {
      setAcord101NamedInsured('');
      setInsuredName('');
      setInsuredAddr1('');
      setInsuredAddr2('');
      setInsuredAddr3('');
      setInsuredCity('');
      setInsuredState('');
      setInsuredZip('');
      return;
    }
    setAcord101NamedInsured(client.name || '');
    setInsuredName(client.name || '');
    setInsuredAddr1(client.name || '');
    setInsuredAddr2(client.addressLine1 || '');
    setInsuredAddr3(client.addressLine2 || '');
    setInsuredCity(client.city || '');
    setInsuredState(client.state || '');
    setInsuredZip(client.zip || '');
  }, [client, selectedPolicies]);

  // Properties API — prefill locationDescription when a property is selected
  const [properties28, setProperties28] = React.useState<{ id: string; address: string; policyIds: string[] }[]>([]);
  React.useEffect(() => {
    if (!insuredId || !selectedPolicies?.length) {
      setProperties28([]);
      return;
    }
    const ncToken = localStorage.getItem('NCToken') || '';
    const igToken = localStorage.getItem('igtoken') || '';
    fetch(
      `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-properties?top=100&insuredDatabaseId=${encodeURIComponent(insuredId)}&policyIds=${encodeURIComponent(selectedPolicies.join(','))}`,
      { headers: { Authorization: `Bearer ${igToken}`, NCToken: ncToken, 'Content-Type': 'application/json' } }
    )
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const items = Array.isArray(data?.value) ? data.value : [];
        setProperties28(items.map((p: any) => ({
          id: p.databaseId,
          address: [p.addressLine1, p.addressLine2, p.city, p.state, p.zip].filter(Boolean).join(', '),
          policyIds: Array.isArray(p.policyIds) ? p.policyIds : [],
        })));
      })
      .catch(() => setProperties28([]));
  }, [insuredId, selectedPolicies]);

  // Prefill locationDescription when selected properties change
  React.useEffect(() => {
    if (!selectedProperties || selectedProperties.length === 0) return;
    const matched = properties28.filter(p => selectedProperties.includes(p.id));
    if (matched.length > 0) {
      setLocationDescription(matched.map(p => p.address).join('\n'));
    }
  }, [selectedProperties, properties28]);


  const editPrefillPending = React.useRef(!!(editCoverages && Array.isArray(editCoverages) && editCoverages.length > 0));
  // Stable flag set once at mount — never changes, never reads stale closure props
  const isEditMode = React.useRef(!!(editCoverages && Array.isArray(editCoverages) && editCoverages.length > 0));
  // A cert saved with no linked policies still has stored coverage rows (blank numbers and
  // limits), but the "restore on first policy change" trigger never fires for it. Track that
  // so the coverage effect can restore it immediately instead (see there).
  const editHasLinkedPolicies = React.useRef(!!(editPolicies && editPolicies.length > 0));

  React.useEffect(() => {
    const noPolicySelected = !selectedPolicies || selectedPolicies.length === 0;

    // Edit mode: on the first policy change, restore the stored coverage XML instead of
    // calling get-coverages (which returns policy defaults, not saved values). A cert saved
    // with no linked policies never gets that first change, so restore it right away; if we
    // waited, the pending flag would survive and the agent's first policy pick would restore
    // the blank stored XML instead of fetching live coverages (Farmers report 9/21, policy
    // 605490300, tracker #241).
    if (editPrefillPending.current && (!noPolicySelected || !editHasLinkedPolicies.current)) {
      editPrefillPending.current = false;
      const p = parseAcord28StoredCoverages(editCoverages!, editPolicies || []);
      if (p.certDate)     setCertDate(p.certDate);
      if (p.producerAddr1) setProducerAddr1(p.producerAddr1);
      if (p.producerAddr2) setProducerAddr2(p.producerAddr2);
      if (p.producerAddr3) setProducerAddr3(p.producerAddr3);
      if (p.producerCity)  setProducerCity(p.producerCity);
      if (p.producerState) setProducerState(p.producerState);
      if (p.producerZip)   setProducerZip(p.producerZip);
      if (p.producerPhone) setProducerPhone(p.producerPhone);
      if (p.producerFax)   setProducerFax(p.producerFax);
      if (p.producerEmail) setProducerEmail(p.producerEmail);
      if (p.companyName) {
        setCompanyName(p.companyName);
        resolveCarrierIdByName(p.companyName).then(id => { if (id) setCompanyNaicId(id); });
      }
      if (p.naicNo)        setNaicNo(p.naicNo);
      if (p.policyType)    setPolicyType(p.policyType);
      if (p.loanNumber)    setLoanNumber(p.loanNumber);
      if (p.policyNumber)  setPolicyNumber(p.policyNumber);
      if (p.effectiveDate)  setEffectiveDate(p.effectiveDate);
      if (p.expirationDate) setExpirationDate(p.expirationDate);
      setContinuedUntilTerminated(p.continuedUntilTerminated);
      if (p.replacesPriorDate) setReplacesPriorDate(p.replacesPriorDate);
      if (p.insuredAddr1)           setInsuredAddr1(p.insuredAddr1);
      if (p.insuredAddr2)           setInsuredAddr2(p.insuredAddr2);
      if (p.insuredAddr3)           setInsuredAddr3(p.insuredAddr3);
      if (p.insuredCity)            setInsuredCity(p.insuredCity);
      if (p.insuredState)           setInsuredState(p.insuredState);
      if (p.insuredZip)             setInsuredZip(p.insuredZip);
      if (p.additionalNamedInsureds) setAdditionalNamedInsureds(p.additionalNamedInsureds);
      if (p.locationDescription)    setLocationDescription(p.locationDescription);
      setPerilsInsured(p.perilsInsured);
      setPerilsBasic(p.perilsBasic);
      setPerilsBroad(p.perilsBroad);
      setPerilsSpecial(p.perilsSpecial);
      setPropBuilding(p.propBuilding);
      setPropBusinessPersonalProp(p.propBusinessPersonalProp);
      setCommPropCovCheck(p.commPropCovCheck);
      if (p.commPropCovText) setCommPropCovText(p.commPropCovText);
      if (p.commPropAmount)  setCommPropAmount(p.commPropAmount);
      if (p.commPropDed)     setCommPropDed(p.commPropDed);
      setBusinessIncome(p.businessIncome);
      setRentalValue(p.rentalValue);
      if (p.birvYesNoNa) setBirvYesNoNa(p.birvYesNoNa);
      if (p.birvLimit)   setBirvLimit(p.birvLimit);
      setBirvActualLoss(p.birvActualLoss);
      if (p.birvMonths)  setBirvMonths(p.birvMonths);
      if (p.blanketCoverage) setBlanketCoverage(p.blanketCoverage);
      if (p.blanketAmount)   setBlanketAmount(p.blanketAmount);
      if (p.terrorismCoverage)          setTerrorismCoverage(p.terrorismCoverage);
      if (p.terrorismSpecificExclusion) setTerrorismSpecificExclusion(p.terrorismSpecificExclusion);
      if (p.domesticTerrorismExcluded)  setDomesticTerrorismExcluded(p.domesticTerrorismExcluded);
      if (p.limitedFungus)      setLimitedFungus(p.limitedFungus);
      if (p.limitedFungusLimit) setLimitedFungusLimit(p.limitedFungusLimit);
      if (p.limitedFungusDed)   setLimitedFungusDed(p.limitedFungusDed);
      if (p.fungusExclusion)     setFungusExclusion(p.fungusExclusion);
      if (p.fungusExclusionForm) setFungusExclusionForm(p.fungusExclusionForm);
      if (p.fungusExclusionDate) setFungusExclusionDate(p.fungusExclusionDate);
      if (p.fungusExclusionText) setFungusExclusionText(p.fungusExclusionText);
      setReplacementCost(p.replacementCost);
      setAgreedValue(p.agreedValue);
      if (p.coinsurance)        setCoinsurance(p.coinsurance);
      if (p.coinsurancePercent) setCoinsurancePercent(p.coinsurancePercent);
      if (p.equipmentBreakdown)      setEquipmentBreakdown(p.equipmentBreakdown);
      if (p.equipmentBreakdownLimit) setEquipmentBreakdownLimit(p.equipmentBreakdownLimit);
      if (p.equipmentBreakdownDed)   setEquipmentBreakdownDed(p.equipmentBreakdownDed);
      if (p.ordinanceOrLaw) setOrdinanceOrLaw(p.ordinanceOrLaw);
      if (p.ordinanceLimit) setOrdinanceLimit(p.ordinanceLimit);
      if (p.ordinanceDed)   setOrdinanceDed(p.ordinanceDed);
      if (p.demolitionCostsYN) setDemolitionCostsYN(p.demolitionCostsYN);
      if (p.demolitionLimit)   setDemolitionLimit(p.demolitionLimit);
      if (p.demolitionDed)     setDemolitionDed(p.demolitionDed);
      if (p.incrCostYN)    setIncrCostYN(p.incrCostYN);
      if (p.incrCostLimit) setIncrCostLimit(p.incrCostLimit);
      if (p.incrCostDed)   setIncrCostDed(p.incrCostDed);
      if (p.earthMovement)      setEarthMovement(p.earthMovement);
      if (p.earthMovementLimit) setEarthMovementLimit(p.earthMovementLimit);
      if (p.earthMovementDed)   setEarthMovementDed(p.earthMovementDed);
      if (p.flood)      setFlood(p.flood);
      if (p.floodLimit) setFloodLimit(p.floodLimit);
      if (p.floodDed)   setFloodDed(p.floodDed);
      setWindHailInclYes(p.windHailInclYes);
      setWindHailInclNo(p.windHailInclNo);
      if (p.windHail)      setWindHail(p.windHail);
      if (p.windHailLimit) setWindHailLimit(p.windHailLimit);
      if (p.windHailDed)   setWindHailDed(p.windHailDed);
      setNamedStormInclYes(p.namedStormInclYes);
      setNamedStormInclNo(p.namedStormInclNo);
      if (p.namedStorm)      setNamedStorm(p.namedStorm);
      if (p.namedStormLimit) setNamedStormLimit(p.namedStormLimit);
      if (p.namedStormDed)   setNamedStormDed(p.namedStormDed);
      if (p.waiverSubrogation)    setWaiverSubrogation(p.waiverSubrogation);
      if (p.additionalInterestName || p.lenderServicingAgent) setAiEnabled(true);
      if (p.additionalInterestName) setAdditionalInterestName(p.additionalInterestName);
      if (p.lenderServicingAgent)   setLenderServicingAgent(p.lenderServicingAgent);
      if (p.authRepSignature)       setAuthRepSignature(p.authRepSignature);
      if (p.remarks)                setRemarks(p.remarks);
      if (p.acord101AdditionalRemarks) setAcord101AdditionalRemarks(p.acord101AdditionalRemarks);
      onLoadingChange?.(false);
      return;
    }

    if (noPolicySelected) return;

    let cancelled = false;

    const fetchAll = async () => {
      onLoadingChange?.(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';

        const [coveragesRes, policyRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'NCToken': ncToken, Authorization: `Bearer ${igToken}` },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId,
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }),
          fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCoi/policy-details-byid`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'NCToken': ncToken, Authorization: `Bearer ${igToken}` },
            body: JSON.stringify({ databaseIds: selectedPolicies }),
          }),
        ]);

        if (cancelled) return;

        const json = await coveragesRes.json();
        const arr: AcordFieldItem[] = Array.isArray(json.d) ? json.d : (Array.isArray(json.Data) ? json.Data : []);

        // Runs whenever the policy selection changes (this branch is never reached on the
        // very first edit-mode load — that's handled by the XML-restore branch above).
        // Prefer the current first-selected policy's address; otherwise fall back to client.
        const policyAddress = pickPolicyAddress(getPolicyAddresses(arr), selectedPolicies[0]);
        if (policyAddress) {
          setInsuredAddr2(policyAddress.AddressLine1 || '');
          setInsuredAddr3(policyAddress.AddressLine2 || '');
          setInsuredCity(policyAddress.City || '');
          setInsuredState(policyAddress.State || '');
          setInsuredZip(policyAddress.ZipCode || '');
        } else if (client) {
          setInsuredAddr2(client.addressLine1 || '');
          setInsuredAddr3(client.addressLine2 || '');
          setInsuredCity(client.city || '');
          setInsuredState(client.state || '');
          setInsuredZip(client.zip || '');
        }

        let covPolicyNumber = '';
        let covEffectiveDate: Date | null = null;
        let covExpirationDate: Date | null = null;

        if (arr.length > 0) {
          const policy = mapFields(arr, ACORD28_POLICY_MAP);
          covPolicyNumber   = policy.policyNumber   || '';
          covEffectiveDate  = policy.effectiveDate  || null;
          covExpirationDate = policy.expirationDate || null;
          if (covPolicyNumber)   setPolicyNumber(covPolicyNumber);
          if (covEffectiveDate)  setEffectiveDate(covEffectiveDate);
          if (covExpirationDate) setExpirationDate(covExpirationDate);

          if (!isV2016) {
          const d = mapFields(arr, ACORD28_MAP);
          const yn = (yes: unknown, no: unknown, na: unknown): 'yes' | 'no' | 'na' | '' =>
            yes ? 'yes' : no ? 'no' : na ? 'na' : '';

          setPerilsInsured(!!d.perilsInsured);
          setPerilsBasic(!!d.perilsBasic);
          setPerilsBroad(!!d.perilsBroad);
          setPerilsSpecial(true);
          setCommPropCovCheck(!!d.perilsOther);
          if (d.perilsOtherDesc) setCommPropCovText(d.perilsOtherDesc as string);
          if (d.commPropAmount)  setCommPropAmount(d.commPropAmount as string);
          if (d.commPropDed)     setCommPropDed(d.commPropDed as string);

          setBusinessIncome(!!d.businessIncome);
          setRentalValue(!!d.rentalValue);
          setBirvYesNoNa(yn(d.birvYes, d.birvNo, d.birvNa));
          if (d.birvLimit)      setBirvLimit(d.birvLimit as string);
          setBirvActualLoss(!!d.birvActualLoss);
          if (d.birvMonths)     setBirvMonths(d.birvMonths as string);

          setBlanketCoverage(yn(d.blanketYes, d.blanketNo, d.blanketNa));
          if (d.blanketAmount)  setBlanketAmount(d.blanketAmount as string);

          setTerrorismCoverage(yn(d.terrorismYes, d.terrorismNo, d.terrorismNa));
          setTerrorismSpecificExclusion(yn(d.terrorismExclYes, d.terrorismExclNo, d.terrorismExclNa));
          setDomesticTerrorismExcluded(yn(d.domesticTerrYes, d.domesticTerrNo, d.domesticTerrNa));

          setLimitedFungus(yn(d.limitedFungusYes, d.limitedFungusNo, d.limitedFungusNa));
          if (d.limitedFungusLimit) setLimitedFungusLimit(d.limitedFungusLimit as string);
          if (d.limitedFungusDed)   setLimitedFungusDed(d.limitedFungusDed as string);

          setFungusExclusion(yn(d.fungusExclYes, d.fungusExclNo, d.fungusExclNa));
          if (d.fungusExclForm) setFungusExclusionForm(d.fungusExclForm as string);
          if (d.fungusExclDate) setFungusExclusionDate(parseLocalDate(d.fungusExclDate as string));
          if (d.fungusExclText) setFungusExclusionText(d.fungusExclText as string);

          setReplacementCost(yn(d.replacementCostYes, d.replacementCostNo, d.replacementCostNa));
          setAgreedValue(yn(d.agreedValueYes, d.agreedValueNo, d.agreedValueNa));

          setCoinsurance(yn(d.coinsuranceYes, d.coinsuranceNo, d.coinsuranceNa));
          if (d.coinsurancePercent) setCoinsurancePercent(d.coinsurancePercent as string);

          setEquipmentBreakdown(yn(d.equipmentYes, d.equipmentNo, d.equipmentNa));
          if (d.equipmentLimit) setEquipmentBreakdownLimit(d.equipmentLimit as string);
          if (d.equipmentDed)   setEquipmentBreakdownDed(d.equipmentDed as string);

          setOrdinanceOrLaw(yn(d.ordinanceYes, d.ordinanceNo, d.ordinanceNa));
          if (d.ordinanceLimit) setOrdinanceLimit(d.ordinanceLimit as string);
          if (d.ordinanceDed)   setOrdinanceDed(d.ordinanceDed as string);

          setDemolitionCostsYN(yn(d.demolitionYes, d.demolitionNo, d.demolitionNa));
          if (d.demolitionLimit) setDemolitionLimit(d.demolitionLimit as string);
          if (d.demolitionDed)   setDemolitionDed(d.demolitionDed as string);

          setIncrCostYN(yn(d.incrCostYes, d.incrCostNo, d.incrCostNa));
          if (d.incrCostLimit) setIncrCostLimit(d.incrCostLimit as string);
          if (d.incrCostDed)   setIncrCostDed(d.incrCostDed as string);

          setEarthMovement(yn(d.earthYes, d.earthNo, d.earthNa));
          if (d.earthLimit) setEarthMovementLimit(d.earthLimit as string);
          if (d.earthDed)   setEarthMovementDed(d.earthDed as string);

          setFlood(yn(d.floodYes, d.floodNo, d.floodNa));
          if (d.floodLimit) setFloodLimit(d.floodLimit as string);
          if (d.floodDed)   setFloodDed(d.floodDed as string);

          setWindHailInclYes(!!d.windHailInclYes);
          setWindHailInclNo(!!d.windHailInclNo);
          setWindHail(yn(d.windHailSubjYes, d.windHailSubjNo, d.windHailNa));
          if (d.windHailLimit) setWindHailLimit(d.windHailLimit as string);
          if (d.windHailDed)   setWindHailDed(d.windHailDed as string);

          setNamedStormInclYes(!!d.namedStormInclYes);
          setNamedStormInclNo(!!d.namedStormInclNo);
          setNamedStorm(yn(d.namedStormSubjYes, d.namedStormSubjNo, d.namedStormNa));
          if (d.namedStormLimit) setNamedStormLimit(d.namedStormLimit as string);
          if (d.namedStormDed)   setNamedStormDed(d.namedStormDed as string);

          setWaiverSubrogation(yn(d.waiverSubrogYes, d.waiverSubrogNo, d.waiverSubrogNa));
          if (d.remarks)                setRemarks(d.remarks as string);
          if (d.additionalInterestName || d.lenderServicingAgent) setAiEnabled(true);
          if (d.additionalInterestName) setAdditionalInterestName(d.additionalInterestName as string);
          if (d.lenderServicingAgent)   setLenderServicingAgent(d.lenderServicingAgent as string);
          } // end if (!isV2016)
        }

        if (policyRes.ok) {
          const policyJson = await policyRes.json();
          const detail = Array.isArray(policyJson.value) ? policyJson.value[0] : null;
          if (detail) {
            if (!covPolicyNumber   && detail.number)         setPolicyNumber(detail.number);
            if (!covEffectiveDate  && detail.effectiveDate)  setEffectiveDate(parseLocalDate(detail.effectiveDate));
            if (!covExpirationDate && detail.expirationDate) setExpirationDate(parseLocalDate(detail.expirationDate));
            if (detail.carrierName) {
              setCompanyName(detail.carrierName);
              resolveCarrierIdByName(detail.carrierName).then(id => { if (id) setCompanyNaicId(id); });
            }
            if (detail.carrierNAIC) setNaicNo(detail.carrierNAIC);
          }
        }

        // Prefill agent contact fields from policy agent API
        const agentContact = await fetchFirstPolicyAgentContact(selectedPolicies);
        if (cancelled) return;
        if (agentContact.phone) setProducerPhone(formatUsPhone(agentContact.phone));
        if (agentContact.fax)   setProducerFax(formatUsPhone(agentContact.fax));
        if (agentContact.email) setProducerEmail(agentContact.email);
        if (agentContact.agentId && agentContact.name) {
          const { npnNumber, addressLine1, city, state, zipCode } = await fetchAgentContact(agentContact.agentId);
          setProducerAddr1(npnNumber ? `${agentContact.name} (${npnNumber})` : agentContact.name);
          if (addressLine1) setProducerAddr2(addressLine1);
          if (city) setProducerCity(city);
          if (state) setProducerState(state);
          if (zipCode) setProducerZip(zipCode);
          const agencyParts = [agentContact.name, addressLine1, city, state, zipCode].filter(Boolean);
          setAcord101Agency(agencyParts.join(', '));
        }
      } catch {
      } finally {
        if (!cancelled) onLoadingChange?.(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; onLoadingChange?.(false); };
  }, [selectedPolicies, insuredId]);

  // With no policy the producer/contact block is blank (Farmers, 9/22): clear it on deselect so
  // a previous pick doesn't linger. Edit mode keeps whatever the stored XML restored.
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) {
      if (isEditMode.current) return;
      setProducerPhone(''); setProducerFax(''); setProducerEmail(''); setProducerAddr1(''); setProducerAddr2(''); setProducerCity(''); setProducerState(''); setProducerZip('');
      setAcord101Agency('');
      return;
    }
    fetchFirstPolicyAgentContact(selectedPolicies).then(async agentContact => {
      if (agentContact.agentId && agentContact.name) {
        const { addressLine1, city, state, zipCode } = await fetchAgentContact(agentContact.agentId);
        const agencyParts = [agentContact.name, addressLine1, city, state, zipCode].filter(Boolean);
        setAcord101Agency(agencyParts.join(', '));
      }
    });
  }, [selectedPolicies]);


  // Refresh the remarks textarea from selected policies' AdditionalNames custom field on
  // every policy selection change — re-derived fully each time, not merged with prior content.
  // Disabled for now — prod team isn't ready for the auto-filled remark yet.
  React.useEffect(() => {
    if (isEditMode.current) return;
    // setAcord101AdditionalRemarks(acord101AdditionalNamedInsureds ? `Additional Named Insured(s): ${acord101AdditionalNamedInsureds}` : '');
  }, [acord101AdditionalNamedInsureds]);

  const prefillPropertyFromBuildingCoverages = (returnObjects: Array<{ fieldName: string; value: string }>) => {
    // Strip F[0].P1[0]. prefix and [0] suffix that NowCerts wraps field names with
    const normalizedMap = new Map<string, string>();
    for (const o of returnObjects) {
      const normalized = o.fieldName.replace(/^F\[\d+\]\.P\d+\[\d+\]\./, '').replace(/\[\d+\]$/, '');
      normalizedMap.set(normalized, o.value);
    }
    const get = (name: string) => normalizedMap.get(name) ?? '';
    const getBool = (name: string) => get(name) === 'Yes';
    // YNN fields: any truthy value on the indicator counts as selected
    const getYNN = (yesField: string, noField: string, naField: string): YesNoNa => {
      if (get(yesField)) return 'yes';
      if (get(noField)) return 'no';
      if (get(naField)) return 'na';
      return '';
    };

    setPropBuilding(getBool('Property_Building_CoverageIndicator_A'));
    setPropBusinessPersonalProp(getBool('Property_BusinessPersonalProperty_CoverageIndicator_A'));

    setPolicyType(get('Policy_PolicyType'));
    setPerilsInsured(getBool('Policy_PolicyType_PerilsInsuredIndicator_A'));
    setPerilsBasic(getBool('Policy_PolicyType_BasicIndicator_A'));
    setPerilsBroad(getBool('Policy_PolicyType_BroadIndicator_A'));
    setPerilsSpecial(true);
    setCommPropCovCheck(getBool('Policy_PolicyType_OtherIndicator_A'));
    setCommPropCovText(get('Policy_PolicyType_OtherDescription_A'));
    setCommPropAmount(get('CommercialPropertyCoverage_Property_LimitAmount_A'));
    setCommPropDed(get('CommercialPropertyCoverage_Property_DeductibleAmount_A'));

    setBusinessIncome(getBool('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_BusinessIncomeCoverageIndicator_A'));
    setRentalValue(getBool('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_RentalValueCoverageIndicator_A'));
    setBirvYesNoNa(getYNN(
      'CommercialPropertyCoverage_BusinessIncomeOrRentalValue_YesIndicator_A',
      'CommercialPropertyCoverage_BusinessIncomeOrRentalValue_NoIndicator_A',
      'CommercialPropertyCoverage_BusinessIncomeOrRentalValue_NotApplicableIndicator_A',
    ));
    setBirvLimit(get('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_LimitAmount_A'));
    setBirvActualLoss(getBool('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_ActualLossSustainedIndicator_A'));
    setBirvMonths(get('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_ActualLossSustainedMonthCount_A'));

    setBlanketCoverage(getYNN('CommercialProperty_Blanket_YesIndicator_A', 'CommercialProperty_Blanket_NoIndicator_A', 'CommercialProperty_Blanket_NotApplicableIndicator_A'));
    setBlanketAmount(get('StatementOfValues_Premises_ValueAmount_A'));

    setTerrorismCoverage(getYNN('CommercialPropertyCoverage_Terrorism_YesIndicator_A', 'CommercialPropertyCoverage_Terrorism_NoIndicator_A', 'CommercialPropertyCoverage_Terrorism_NotApplicableIndicator_A'));
    setTerrorismSpecificExclusion(getYNN('CommercialPropertyCoverage_TerrorismExclusion_YesIndicator_A', 'CommercialPropertyCoverage_TerrorismExclusion_NoIndicator_A', 'CommercialPropertyCoverage_TerrorismExclusion_NotApplicableIndicator_A'));
    setDomesticTerrorismExcluded(getYNN('CommercialPropertyCoverage_DomesticTerrorismExclusion_YesIndicator_A', 'CommercialPropertyCoverage_DomesticTerrorismExclusion_NoIndicator_A', 'CommercialPropertyCoverage_DomesticTerrorismExclusion_NotApplicableIndicator_A'));

    setLimitedFungus(getYNN('CommercialPropertyCoverage_LimitedFungus_YesIndicator_A', 'CommercialPropertyCoverage_LimitedFungus_NoIndicator_A', 'CommercialPropertyCoverage_LimitedFungus_NotApplicableIndicator_A'));
    setLimitedFungusLimit(get('CommercialPropertyCoverage_LimitedFungus_LimitAmount_A'));
    setLimitedFungusDed(get('CommercialPropertyCoverage_LimitedFungus_DeductibleAmount_A'));

    setFungusExclusion(getYNN('CommercialPropertyCoverage_FungusExclusion_YesIndicator_A', 'CommercialPropertyCoverage_FungusExclusion_NoIndicator_A', 'CommercialPropertyCoverage_FungusExclusion_NotApplicableIndicator_A'));
    setFungusExclusionForm(get('FormEndorsement_FormIdentifier_A'));
    const feDate = get('FormEndorsement_FormDate_A');
    setFungusExclusionDate(feDate ? (() => { const d = new Date(feDate); return isNaN(d.getTime()) ? null : d; })() : null);
    setFungusExclusionText(get('FormEndorsement_CopyrightOwnerCode_A'));

    setReplacementCost(getYNN('CommercialPropertyCoverage_ReplacementCost_YesIndicator_A', 'CommercialPropertyCoverage_ReplacementCost_NoIndicator_A', 'CommercialPropertyCoverage_ReplacementCost_NotApplicableIndicator_A'));
    setAgreedValue(getYNN('CommercialPropertyCoverage_AgreedValue_YesIndicator_A', 'CommercialPropertyCoverage_AgreedValue_NoIndicator_A', 'CommercialPropertyCoverage_AgreedValue_NotApplicableIndicator_A'));

    setCoinsurance(getYNN('CommercialPropertyCoverage_Coinsurance_YesIndicator_A', 'CommercialPropertyCoverage_Coinsurance_NoIndicator_A', 'CommercialPropertyCoverage_Coinsurance_NotApplicableIndicator_A'));
    setCoinsurancePercent(get('CommercialProperty_Premises_CoinsurancePercent_A'));

    setEquipmentBreakdown(getYNN('CommercialPropertyCoverage_EquipmentBreakdown_YesIndicator_A', 'CommercialPropertyCoverage_EquipmentBreakdown_NoIndicator_A', 'CommercialPropertyCoverage_EquipmentBreakdown_NotApplicableIndicator_A'));
    setEquipmentBreakdownLimit(get('CommercialPropertyCoverage_EquipmentBreakdown_LimitAmount_A'));
    setEquipmentBreakdownDed(get('CommercialPropertyCoverage_EquipmentBreakdown_DeductibleAmount_A'));

    setOrdinanceOrLaw(getYNN('CommercialProperty_BuildingOrdinanceOrLaw_UndamagedPortionOfBuildingYesIndicator_A', 'CommercialProperty_BuildingOrdinanceOrLaw_UndamagedPortionOfBuildingNoIndicator_A', 'CommercialProperty_BuildingOrdinanceOrLaw_UndamagedPortionOfBuildingNotApplicableIndicator_A'));
    setOrdinanceLimit(get('CommercialPropertyCoverage_BuildingOrdinanceOrLaw_UndamagedPortionBuildingLimitAmount_A'));
    setOrdinanceDed(get('CommercialPropertyCoverage_BuildingOrdinanceOrLaw_UndamagedPortionBuildingDeductibleAmount_A'));

    setDemolitionCostsYN(getYNN('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsYesIndicator_A', 'CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsNoIndicator_A', 'CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsNotApplicableIndicator_A'));
    setDemolitionLimit(get('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsLimitAmount_A'));
    setDemolitionDed(get('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsDeductibleAmount_A'));

    setIncrCostYN(getYNN('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionYesIndicator_A', 'CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionNoIndicator_A', 'CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionNotApplicableIndicator_A'));
    setIncrCostLimit(get('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionLimitAmount_A'));
    setIncrCostDed(get('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionDeductibleAmount_A'));

    setEarthMovement(getYNN('CommercialPropertyCoverage_EarthMovement_YesIndicator_A', 'CommercialPropertyCoverage_EarthMovement_NoIndicator_A', 'CommercialPropertyCoverage_EarthMovement_NotApplicableIndicator_A'));
    setEarthMovementLimit(get('CommercialPropertyCoverage_EarthMovement_LimitAmount_A'));
    setEarthMovementDed(get('CommercialPropertyCoverage_EarthMovement_DeductibleAmount_A'));

    setFlood(getYNN('CommercialPropertyCoverage_Flood_YesIndicator_A', 'CommercialPropertyCoverage_Flood_NoIndicator_A', 'CommercialPropertyCoverage_Flood_NotApplicableIndicator_A'));
    setFloodLimit(get('CommercialPropertyCoverage_Flood_LimitAmount_A'));
    setFloodDed(get('CommercialPropertyCoverage_Flood_DeductibleAmount_A'));

    setWindHailInclYes(getBool('CommercialPropertyCoverage_WindHail_YesIndicator_A'));
    setWindHailInclNo(getBool('CommercialPropertyCoverage_WindHail_NoIndicator_A'));
    setWindHail(getYNN('CommercialPropertyCoverage_WindHail_SubjectToProvisionsIndicator_A', 'CommercialPropertyCoverage_WindHail_SubjectToProvisionsNoIndicator_A', 'CommercialPropertyCoverage_WindHail_NotApplicableIndicator_A'));
    setWindHailLimit(get('CommercialPropertyCoverage_WindHail_LimitAmount_A'));
    setWindHailDed(get('CommercialPropertyCoverage_WindHail_DeductibleAmount_A'));

    setNamedStormInclYes(getBool('CommercialPropertyCoverage_NamedWindstorm_YesIndicator_A'));
    setNamedStormInclNo(getBool('CommercialPropertyCoverage_NamedWindstorm_NoIndicator_A'));
    setNamedStorm(getYNN('CommercialPropertyCoverage_NamedWindstorm_SubjectToProvisionsIndicator_A', 'CommercialPropertyCoverage_NamedWindstorm_SubjectToProvisionsNoIndicator_A', 'CommercialPropertyCoverage_NamedWindstorm_SubjectToProvisionsNotApplicableIndicator_A'));
    setNamedStormLimit(get('CommercialPropertyCoverage_NamedWindstorm_LimitAmount_A'));
    setNamedStormDed(get('CommercialPropertyCoverage_NamedWindstorm_DeductibleAmount_A'));

    setWaiverSubrogation(getYNN('CommercialProperty_SubrogationWaiver_YesIndicator_A', 'CommercialProperty_SubrogationWaiver_NoIndicator_A', 'CommercialProperty_SubrogationWaiver_NotApplicableIndicator_A'));
    setRemarks(get('EvidenceCommercialProperty_A'));
  };

  React.useImperativeHandle(ref, () => ({
    applyBuildingCoverages: prefillPropertyFromBuildingCoverages,
    getFormData: (): Acord28FormData => ({
      certDate, producerAddr1, producerAddr2, producerAddr3,
      producerCity, producerState, producerZip, producerPhone, producerFax, producerEmail,
      companyName, naicNo, policyType, loanNumber, policyNumber,
      effectiveDate, expirationDate, continuedUntilTerminated, replacesPriorDate,
      insuredAddr1, insuredAddr2, insuredAddr3, insuredCity, insuredState, insuredZip,
      additionalNamedInsureds, propBuilding, propBusinessPersonalProp, locationDescription,
      perilsInsured, perilsBasic, perilsBroad, perilsSpecial,
      commPropCovCheck, commPropCovText, commPropAmount, commPropDed,
      businessIncome, rentalValue, birvYesNoNa, birvLimit, birvActualLoss, birvMonths,
      blanketCoverage, blanketAmount,
      terrorismCoverage, terrorismSpecificExclusion, domesticTerrorismExcluded,
      limitedFungus, limitedFungusLimit, limitedFungusDed,
      fungusExclusion, fungusExclusionForm, fungusExclusionDate, fungusExclusionText,
      replacementCost, agreedValue,
      coinsurance, coinsurancePercent,
      equipmentBreakdown, equipmentBreakdownLimit, equipmentBreakdownDed,
      ordinanceOrLaw, ordinanceLimit, ordinanceDed,
      demolitionCostsYN, demolitionLimit, demolitionDed,
      incrCostYN, incrCostLimit, incrCostDed,
      earthMovement, earthMovementLimit, earthMovementDed,
      flood, floodLimit, floodDed,
      windHailInclYes, windHailInclNo, windHail, windHailLimit, windHailDed,
      namedStormInclYes, namedStormInclNo, namedStorm, namedStormLimit, namedStormDed,
      waiverSubrogation,
      additionalInterestName, lenderServicingAgent, authRepSignature, remarks,
      companyNaicId: companyNaicId ?? lookupCarrierIdSync(companyName),
      showAcord101,
      acord101Agency,
      acord101NamedInsured,
      acord101AdditionalRemarks,
    }),
  }));


  const renderYesNoNa = (id: string, value: YesNoNa, onChange: (v: YesNoNa) => void) => (
    <>
      <RadioButton inputId={`${id}_yes`} name={id} value="yes" checked={value === 'yes'} onChange={() => onChange('yes')} />
      <RadioButton inputId={`${id}_no`} name={id} value="no" checked={value === 'no'} onChange={() => onChange('no')} />
      <RadioButton inputId={`${id}_na`} name={id} value="na" checked={value === 'na'} onChange={() => onChange('na')} />
    </>
  );

  return (
    <>
      <div className="masterCert__section">
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">EVIDENCE OF COMMERCIAL PROPERTY INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer">
          This evidence of commercial property insurance is issued as a matter of information only and confers no rights upon the additional interest named below. This evidence of commercial property insurance does not amend, extend or alter the coverage afforded by the policies below.
        </div>

        <div className="masterCert__twoColumnLayout">
          <div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__sectionHeader">PRODUCER NAME, CONTACT PERSON AND ADDRESS</div>
              <InputText maxLength={100} value={producerAddr1} onChange={(e) => setProducerAddr1(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={producerAddr2} onChange={(e) => setProducerAddr2(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={producerAddr3} onChange={(e) => setProducerAddr3(e.target.value)} placeholder="Suite / Unit" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={producerCity} onChange={(e) => setProducerCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={50} value={producerState} onChange={(e) => setProducerState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={20} value={producerZip} onChange={(e) => setProducerZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div style={{ marginBottom: 6 }}>
              <div className="masterCert__contactLabel">PHONE:</div>
              <InputText value={formatUsPhone(producerPhone)} onChange={(e) => setProducerPhone(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">FAX:</div>
                <InputText value={formatUsPhone(producerFax)} onChange={(e) => setProducerFax(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">EMAIL:</div>
                <InputText maxLength={100} value={producerEmail} onChange={(e) => setProducerEmail(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">CODE:</div>
                <InputText maxLength={30} value={producerCode} onChange={(e) => setProducerCode(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly style={{ opacity: 0.5, cursor: 'not-allowed', background: '#f3f4f6' }} />
              </div>
              <div>
                <div className="masterCert__contactLabel">SUB CODE:</div>
                <InputText maxLength={30} value={producerSubCode} onChange={(e) => setProducerSubCode(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly style={{ opacity: 0.5, cursor: 'not-allowed', background: '#f3f4f6' }} />
              </div>
            </div>
            <div>
              <div className="masterCert__contactLabel">AGENCY CUSTOMER ID #:</div>
              <InputText maxLength={30} value={agencyCustomerId} onChange={(e) => setAgencyCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly style={{ opacity: 0.5, cursor: 'not-allowed', background: '#f3f4f6' }} />
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>COMPANY NAME AND ADDRESS</div>
                <Button className="carrierDialog__secondaryBtn" label="Add New" icon="pi pi-plus" outlined size="small" style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <InsurerNaicField name={companyName} id={companyNaicId} naic={naicNo} onNameChange={(name) => { setCompanyName(name); setCompanyNaicId(null); }} onIdChange={setCompanyNaicId} onNaicChange={setNaicNo} className="masterCert__input masterCert__inputFull" />
              <div style={{ color: '#ba170b', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', margin: '8px 0' }}>
                IF MULTIPLE COMPANIES, COMPLETE SEPARATE FORM FOR EACH
              </div>
            </div>

            <div style={{ marginBottom: 6 }}>
              <div className="masterCert__contactLabel">POLICY TYPE</div>
              <InputText maxLength={100} value={policyType} onChange={(e) => setPolicyType(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">LOAN NUMBER</div>
                <InputText maxLength={30} value={loanNumber} onChange={(e) => setLoanNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">POLICY NUMBER</div>
                <InputText maxLength={50} value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">EFFECTIVE DATE</div>
                <div className="masterCert__dateCell">
                  <Calendar value={effectiveDate} onChange={(e) => setEffectiveDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
              <div>
                <div className="masterCert__contactLabel">EXPIRATION DATE</div>
                <div className="masterCert__dateCell">
                  <Calendar value={expirationDate} onChange={(e) => setExpirationDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
            </div>
            <div className="masterCert__checkboxItem" style={{ marginBottom: 8, marginTop: 8 }}>
              <Checkbox inputId="a28ContTerminated" checked={continuedUntilTerminated} onChange={(e) => setContinuedUntilTerminated(e.checked ?? true)} />
              <label htmlFor="a28ContTerminated" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>CONTINUED UNTIL TERMINATED IF CHECKED</label>
            </div>
            <div>
              <div className="masterCert__contactLabel">THIS REPLACES PRIOR EVIDENCE DATED:</div>
              <div className="masterCert__dateCell">
                <Calendar value={replacesPriorDate} onChange={(e) => setReplacesPriorDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
              </div>
            </div>
          </div>
        </div>

        {/* ═══ INSURED ═══ */}
        <div style={{ marginBottom: 16 }}>
          <div className="masterCert__sectionHeader">INSURED</div>
          <InputText maxLength={100} value={insuredAddr1} onChange={(e) => setInsuredAddr1(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
          <InputText maxLength={100} value={insuredAddr2} onChange={(e) => setInsuredAddr2(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
          <InputText maxLength={100} value={insuredAddr3} onChange={(e) => setInsuredAddr3(e.target.value)} placeholder="Suite / Unit" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
          <div className="masterCert__addressGrid">
            <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
            <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
            <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
          </div>
          <div style={{ marginTop: 8 }}>
            <div className="masterCert__contactLabel">ADDITIONAL NAMED INSURED(S)</div>
            <InputText maxLength={100} value={additionalNamedInsureds} onChange={(e) => setAdditionalNamedInsureds(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        {/* ═══ PROPERTY INFORMATION ═══ */}
        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">PROPERTY INFORMATION</div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a28Building" checked={propBuilding} onChange={(e) => setPropBuilding(e.checked ?? false)} />
              <label htmlFor="a28Building" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>BUILDING</label>
            </div>
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a28BPP" checked={propBusinessPersonalProp} onChange={(e) => setPropBusinessPersonalProp(e.checked ?? false)} />
              <label htmlFor="a28BPP" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>BUSINESS PERSONAL PROPERTY</label>
            </div>
          </div>
          <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>LOCATION/DESCRIPTION</div>
          <InputTextarea maxLength={2000} value={locationDescription} onChange={(e) => setLocationDescription(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        {/* Policy disclaimer */}
        <div className="masterCert__disclaimer" style={{ marginBottom: 12 }}>
          The policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this evidence of property insurance may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions, and conditions of such policies. Limits shown may have been reduced by paid claims.
        </div>

        {/* ═══ COVERAGE INFORMATION ═══ */}
        <div className="masterCert__coveragesSection" style={{ borderTop: 'none', paddingTop: 0 }}>
          <div className="masterCert__coveragesTitle">COVERAGE INFORMATION</div>

          {/* Perils checkboxes */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <div className="masterCert__checkboxItem">
              <span className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem', fontWeight: 600 }}>PERILS INSURED</span>
            </div>
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a28Basic" checked={perilsBasic} onChange={(e) => setPerilsBasic(e.checked ?? false)} />
              <label htmlFor="a28Basic" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>BASIC</label>
            </div>
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a28Broad" checked={perilsBroad} onChange={(e) => setPerilsBroad(e.checked ?? false)} />
              <label htmlFor="a28Broad" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>BROAD</label>
            </div>
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a28Special" checked={perilsSpecial} onChange={(e) => setPerilsSpecial(e.checked ?? false)} />
              <label htmlFor="a28Special" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>SPECIAL</label>
            </div>
          </div>

          {/* Coverage checkbox + text row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Checkbox inputId="a28CommPropCov" checked={commPropCovCheck} onChange={(e) => setCommPropCovCheck(e.checked ?? false)} />
            <InputText value={commPropCovText} onChange={(e) => setCommPropCovText(e.target.value)} className="masterCert__input" style={{ flex: 1 }} />
          </div>

          {/* COMMERCIAL PROPERTY COVERAGE AMOUNT */}
          <div className="acord28__amountRow">
            <span className="acord28__amountLabel">COMMERCIAL PROPERTY COVERAGE AMOUNT OF INSURANCE:</span>
            <span style={{ color: '#0057A4', fontWeight: 600 }}>$</span>
            <InputText value={commPropAmount} onChange={(e) => setCommPropAmount(e.target.value)} className="masterCert__input" style={{ width: 140 }} />
            <span style={{ color: '#0057A4', fontWeight: 600 }}>DED:</span>
            <InputText value={commPropDed} onChange={(e) => setCommPropDed(e.target.value)} className="masterCert__input" style={{ width: 140 }} />
          </div>

          {/* ═══ YES / NO / N/A TABLE ═══ */}
          {/* Table Header */}
          <div className="acord28__ynnHeader">
            <div></div>
            <div>YES</div>
            <div>NO</div>
            <div>N/A</div>
            <div></div>
          </div>

          {/* BUSINESS INCOME / RENTAL VALUE */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div className="masterCert__checkboxItem" style={{ marginBottom: 0 }}>
                <Checkbox inputId="a28BI" checked={businessIncome} onChange={(e) => setBusinessIncome(e.checked ?? false)} />
                <label htmlFor="a28BI" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem', fontWeight: 600 }}>BUSINESS INCOME</label>
              </div>
              <div className="masterCert__checkboxItem" style={{ marginBottom: 0 }}>
                <Checkbox inputId="a28RV" checked={rentalValue} onChange={(e) => setRentalValue(e.checked ?? false)} />
                <label htmlFor="a28RV" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem', fontWeight: 600 }}>RENTAL VALUE</label>
              </div>
            </div>
            {renderYesNoNa('birv', birvYesNoNa, setBirvYesNoNa)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={birvLimit} onChange={(e) => setBirvLimit(e.target.value)} className="masterCert__input" style={{ width: 80 }} />
              <InputText value={birvLimit2} onChange={(e) => setBirvLimit2(e.target.value)} className="masterCert__input" style={{ width: 80 }} />
              <div className="masterCert__checkboxItem" style={{ marginBottom: 0 }}>
                <Checkbox inputId="a28ActualLoss" checked={birvActualLoss} onChange={(e) => setBirvActualLoss(e.checked ?? false)} />
                <label htmlFor="a28ActualLoss" className="masterCert__checkboxItemLabel">Actual Loss</label>
              </div>
              <span className="acord28__ynnExtraLabel"># of months:</span>
              <InputText value={birvMonths} onChange={(e) => setBirvMonths(e.target.value)} className="masterCert__input" style={{ width: 50 }} />
            </div>
          </div>

          {/* BLANKET COVERAGE */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>BLANKET COVERAGE</strong></div>
            {renderYesNoNa('blanket', blanketCoverage, setBlanketCoverage)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel" style={{ fontSize: '0.7rem' }}>If YES, indicate value(s) reported on property identified above:</span>
              <span style={{ color: '#0057A4', fontWeight: 600 }}>$</span>
              <InputText value={blanketAmount} onChange={(e) => setBlanketAmount(e.target.value)} className="masterCert__input" style={{ width: 120 }} />
            </div>
          </div>

          {/* TERRORISM COVERAGE */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>TERRORISM COVERAGE</strong></div>
            {renderYesNoNa('terrorism', terrorismCoverage, setTerrorismCoverage)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">Attach Disclosure Notice / DEC</span>
            </div>
          </div>

          {/* IS THERE A TERRORISM-SPECIFIC EXCLUSION? */}
          <div className="acord28__ynnRow acord28__ynnRow--sub">
            <div className="acord28__ynnLabel" style={{ paddingLeft: 16 }}>IS THERE A TERRORISM-SPECIFIC EXCLUSION?</div>
            {renderYesNoNa('terrorismExcl', terrorismSpecificExclusion, setTerrorismSpecificExclusion)}
            <div></div>
          </div>

          {/* IS DOMESTIC TERRORISM EXCLUDED? */}
          <div className="acord28__ynnRow acord28__ynnRow--sub">
            <div className="acord28__ynnLabel" style={{ paddingLeft: 16 }}>IS DOMESTIC TERRORISM EXCLUDED?</div>
            {renderYesNoNa('domesticTerrorism', domesticTerrorismExcluded, setDomesticTerrorismExcluded)}
            <div></div>
          </div>

          {/* LIMITED FUNGUS COVERAGE */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>LIMITED FUNGUS COVERAGE</strong></div>
            {renderYesNoNa('limitedFungus', limitedFungus, setLimitedFungus)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={limitedFungusLimit} onChange={(e) => setLimitedFungusLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={limitedFungusDed} onChange={(e) => setLimitedFungusDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* FUNGUS EXCLUSION */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>FUNGUS EXCLUSION</strong> <span style={{ fontWeight: 400, fontSize: '0.7rem' }}>(If "YES", specify organization's form used)</span></div>
            {renderYesNoNa('fungusExcl', fungusExclusion, setFungusExclusion)}
            <div className="acord28__ynnExtra">
              <InputText value={fungusExclusionForm} onChange={(e) => setFungusExclusionForm(e.target.value)} className="masterCert__input" style={{ width: 120 }} />
              <div className="masterCert__dateCell" style={{ width: 130 }}>
                <Calendar value={fungusExclusionDate} onChange={(e) => setFungusExclusionDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
              </div>
              <InputText value={fungusExclusionText} onChange={(e) => setFungusExclusionText(e.target.value)} className="masterCert__input" style={{ width: 120 }} />
            </div>
          </div>

          {/* REPLACEMENT COST */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>REPLACEMENT COST</strong></div>
            {renderYesNoNa('replacementCost', replacementCost, setReplacementCost)}
            <div></div>
          </div>

          {/* AGREED VALUE */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>AGREED VALUE</strong></div>
            {renderYesNoNa('agreedValue', agreedValue, setAgreedValue)}
            <div></div>
          </div>

          {/* COINSURANCE */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>COINSURANCE</strong></div>
            {renderYesNoNa('coinsurance', coinsurance, setCoinsurance)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES,</span>
              <InputText value={coinsurancePercent} onChange={(e) => setCoinsurancePercent(e.target.value)} className="masterCert__input" style={{ width: 140 }} />
              <span>%</span>
            </div>
          </div>

          {/* EQUIPMENT BREAKDOWN */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>EQUIPMENT BREAKDOWN</strong> <span style={{ fontWeight: 400, fontSize: '0.7rem' }}>(If Applicable)</span></div>
            {renderYesNoNa('equipBreakdown', equipmentBreakdown, setEquipmentBreakdown)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={equipmentBreakdownLimit} onChange={(e) => setEquipmentBreakdownLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={equipmentBreakdownDed} onChange={(e) => setEquipmentBreakdownDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* ORDINANCE OR LAW */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>ORDINANCE OR LAW</strong></div>
            {renderYesNoNa('ordinance', ordinanceOrLaw, setOrdinanceOrLaw)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={ordinanceLimit} onChange={(e) => setOrdinanceLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={ordinanceDed} onChange={(e) => setOrdinanceDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* - Coverage for loss to undamaged portion of bldg */}
          <div className="acord28__ynnRow acord28__ynnRow--sub">
            <div className="acord28__ynnLabel" style={{ paddingLeft: 16, fontSize: '0.75rem' }}>- Coverage for loss to undamaged portion of bldg</div>
            <div></div><div></div><div></div>
            <div></div>
          </div>

          {/* - Demolition Costs */}
          <div className="acord28__ynnRow acord28__ynnRow--sub">
            <div className="acord28__ynnLabel" style={{ paddingLeft: 16 }}>- Demolition Costs</div>
            {renderYesNoNa('demolition', demolitionCostsYN, setDemolitionCostsYN)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={demolitionLimit} onChange={(e) => setDemolitionLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={demolitionDed} onChange={(e) => setDemolitionDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* - Incr. Cost of Construction */}
          <div className="acord28__ynnRow acord28__ynnRow--sub">
            <div className="acord28__ynnLabel" style={{ paddingLeft: 16 }}>- Incr. Cost of Construction</div>
            {renderYesNoNa('incrCost', incrCostYN, setIncrCostYN)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={incrCostLimit} onChange={(e) => setIncrCostLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={incrCostDed} onChange={(e) => setIncrCostDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* EARTH MOVEMENT */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>EARTH MOVEMENT</strong> <span style={{ fontWeight: 400, fontSize: '0.7rem' }}>(If Applicable)</span></div>
            {renderYesNoNa('earthMove', earthMovement, setEarthMovement)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={earthMovementLimit} onChange={(e) => setEarthMovementLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={earthMovementDed} onChange={(e) => setEarthMovementDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* FLOOD */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>FLOOD</strong> <span style={{ fontWeight: 400, fontSize: '0.7rem' }}>(If Applicable)</span></div>
            {renderYesNoNa('flood', flood, setFlood)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={floodLimit} onChange={(e) => setFloodLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={floodDed} onChange={(e) => setFloodDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* WIND / HAIL INCL */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel">
              <strong>WIND / HAIL INCL</strong>
              <div className="masterCert__checkboxItem" style={{ display: 'inline-flex', marginLeft: 8, marginBottom: 0 }}>
                <Checkbox inputId="a28WHYes" checked={windHailInclYes} onChange={(e) => setWindHailInclYes(e.checked ?? false)} />
                <label htmlFor="a28WHYes" className="masterCert__checkboxItemLabel">Yes</label>
              </div>
              <div className="masterCert__checkboxItem" style={{ display: 'inline-flex', marginLeft: 8, marginBottom: 0 }}>
                <Checkbox inputId="a28WHNo" checked={windHailInclNo} onChange={(e) => setWindHailInclNo(e.checked ?? false)} />
                <label htmlFor="a28WHNo" className="masterCert__checkboxItemLabel">No</label>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#666', paddingLeft: 0 }}>Subject to Different Provisions:</div>
            </div>
            {renderYesNoNa('windHail', windHail, setWindHail)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={windHailLimit} onChange={(e) => setWindHailLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={windHailDed} onChange={(e) => setWindHailDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>


          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel">
              <strong>NAMED STORM INCL</strong>
              <div className="masterCert__checkboxItem" style={{ display: 'inline-flex', marginLeft: 8, marginBottom: 0 }}>
                <Checkbox inputId="a28NSYes" checked={namedStormInclYes} onChange={(e) => setNamedStormInclYes(e.checked ?? false)} />
                <label htmlFor="a28NSYes" className="masterCert__checkboxItemLabel">Yes</label>
              </div>
              <div className="masterCert__checkboxItem" style={{ display: 'inline-flex', marginLeft: 8, marginBottom: 0 }}>
                <Checkbox inputId="a28NSNo" checked={namedStormInclNo} onChange={(e) => setNamedStormInclNo(e.checked ?? false)} />
                <label htmlFor="a28NSNo" className="masterCert__checkboxItemLabel">No</label>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#666', paddingLeft: 0 }}>Subject to Different Provisions:</div>
            </div>
            {renderYesNoNa('namedStorm', namedStorm, setNamedStorm)}
            <div className="acord28__ynnExtra">
              <span className="acord28__ynnExtraLabel">If YES, LIMIT:</span>
              <InputText value={namedStormLimit} onChange={(e) => setNamedStormLimit(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
              <span className="acord28__ynnExtraLabel">DED:</span>
              <InputText value={namedStormDed} onChange={(e) => setNamedStormDed(e.target.value)} className="masterCert__input" style={{ width: 100 }} />
            </div>
          </div>

          {/* PERMISSION TO WAIVE SUBROGATION */}
          <div className="acord28__ynnRow">
            <div className="acord28__ynnLabel"><strong>PERMISSION TO WAIVE SUBROGATION IN FAVOR OF MORTGAGE HOLDER PRIOR TO LOSS</strong></div>
            {renderYesNoNa('waiverSubrog', waiverSubrogation, setWaiverSubrogation)}
            <div></div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="masterCert__sectionHeader">CANCELLATION</div>
          <div className="masterCert__cancellationText">
            Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
          </div>
        </div>

        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox" style={!aiEnabled ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', minHeight: 140 } : {}}>
            {!aiEnabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0, color: '#9ca3af' }}>ADDITIONAL INTEREST</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11px', color: '#9ca3af' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '11px' }} />
                  <span>Additional interest can be added here.</span>
                </div>
                <Button
                  label="Enable"
                  icon="pi pi-lock-open"
                  size="small"
                  outlined
                  style={{ height: '28px', fontSize: '11px', padding: '0 14px', borderRadius: '4px', fontWeight: 500 }}
                  onClick={() => setAiEnabled(true)}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>ADDITIONAL INTEREST</div>
                  <Button
                    label="Disable"
                    icon="pi pi-lock"
                    size="small"
                    style={{ height: '22px', fontSize: '10px', padding: '0 8px', borderRadius: '4px', fontWeight: 500 }}
                    onClick={() => { setAiEnabled(false); setAdditionalInterestName(''); setLenderServicingAgent(''); }}
                  />
                </div>
                <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>NAME AND ADDRESS</div>
                <InputTextarea maxLength={2000} value={additionalInterestName} onChange={(e) => setAdditionalInterestName(e.target.value)} rows={5} className="masterCert__descriptionTextarea" />
              </>
            )}
          </div>
          <div className="masterCert__cancellationBox">
            {aiEnabled && <>
              <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>LENDER SERVICING AGENT NAME AND ADDRESS</div>
              <InputTextarea maxLength={2000} value={lenderServicingAgent} onChange={(e) => setLenderServicingAgent(e.target.value)} rows={3} className="masterCert__descriptionTextarea" />
            </>}
            <div className="masterCert__authRepLabel" style={{ marginTop: 12 }}>AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button label="Create New" className="p-button-outlined carrierDialog__secondaryBtn" onClick={() => setShowAddSignature(true)} />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">EVIDENCE OF COMMERCIAL PROPERTY INSURANCE REMARKS - Including Special Conditions (Use only if more space is required)</div>
          <InputTextarea maxLength={2000} value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        <div className="masterCert__acordFooter">
          <div>ACORD 28 (2016/03)</div>
          <div>&copy; 2003-2015 ACORD CORPORATION. All rights reserved.</div>
        </div>

        {/* ── ACORD 101 - Additional Remarks Schedule ── */}
        {showAcord101 && (
          <div className="masterCert__acord101">
            <div className="masterCert__acordHeader" style={{ marginTop: 24 }}>
              <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
              <div className="masterCert__acordTitleWrapper">
                <div className="masterCert__acordTitle">ADDITIONAL REMARKS SCHEDULE</div>
              </div>
            </div>
            <div className="masterCert__twoColumnLayout">
              <div>
                <div className="masterCert__sectionHeader">AGENCY</div>
                <InputTextarea maxLength={250} value={acord101Agency} onChange={(e) => setAcord101Agency(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Agency name and address" />
              </div>
              <div>
                <div className="masterCert__sectionHeader">NAMED INSURED</div>
                <InputTextarea maxLength={100} value={acord101NamedInsured} onChange={(e) => setAcord101NamedInsured(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Named insured" />
              </div>
            </div>
            <div className="masterCert__descriptionSection">
              <div className="masterCert__descriptionTitle">ADDITIONAL REMARKS</div>
              <InputTextarea maxLength={2000} value={acord101AdditionalRemarks} onChange={(e) => setAcord101AdditionalRemarks(e.target.value)} rows={8} className="masterCert__descriptionTextarea" placeholder="Enter additional remarks..." />
            </div>
            <div className="masterCert__acordFooter">
              <div>ACORD 101 (2008/01)</div>
              <div>&copy; 2008 ACORD CORPORATION. All rights reserved.</div>
            </div>
          </div>
        )}
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
});

export default Acord28Form;
